import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Logo from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

export default function Login() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { user, login, register, googleLogin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : user.role === "team" ? "/tracker" : "/");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = mode === "login" ? await login(form.email, form.password) : await register(form.name, form.email, form.password);
      toast.success(`Kia ora, ${u.name || "friend"}.`);
      navigate(u.role === "admin" ? "/admin" : u.role === "team" ? "/tracker" : "/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#090A0F] grain relative flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md bg-[#12141C] border border-[#212636] rounded-2xl p-8" data-testid="login-page">
        <Link to="/" className="flex items-center gap-3 mb-8" data-testid="login-home-link">
          <Logo size={30} />
          <span className="font-display font-extrabold uppercase text-white text-sm">No To Meth</span>
        </Link>

        <h1 className="font-display font-extrabold uppercase text-2xl text-white tracking-tight mb-1">
          {mode === "login" ? "Welcome back" : "Join the movement"}
        </h1>
        <p className="text-xs text-slate-500 mb-6">Members can track their pledge and stories. Admins review submissions.</p>

        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name"
              data-testid="register-input-name" maxLength={80}
              className="w-full bg-[#090A0F] border border-[#212636] rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#EF4444]" />
          )}
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email"
            data-testid="login-input-email"
            className="w-full bg-[#090A0F] border border-[#212636] rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#EF4444]" />
          <input required type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder={mode === "login" ? "Password" : "Password (8+ characters)"} data-testid="login-input-password"
            className="w-full bg-[#090A0F] border border-[#212636] rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#EF4444]" />
          {error && <p className="text-xs text-[#EF4444]" data-testid="login-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="login-submit-button"
            className="w-full bg-[#EF4444] hover:bg-[#DC2626] disabled:opacity-50 transition-colors text-white font-bold rounded-lg py-3 text-sm">
            {busy ? "One moment…" : mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>

        <div className="flex items-center gap-3 my-5">
          <span className="flex-1 h-px bg-[#212636]" />
          <span className="mono-label text-slate-600">or</span>
          <span className="flex-1 h-px bg-[#212636]" />
        </div>

        <button onClick={googleLogin} data-testid="google-signin-button"
          className="w-full bg-white hover:bg-slate-200 transition-colors text-black font-bold rounded-lg py-3 text-sm flex items-center justify-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.5h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.3-2.1 3.7-5.2 3.7-9z"/><path fill="#34A853" d="M12 24c3.2 0 6-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-6-2.2-7-5.1l-3.9 3C3.2 21.3 7.3 24 12 24z"/><path fill="#FBBC05" d="M5 14.3c-.2-.7-.4-1.5-.4-2.3s.2-1.6.4-2.3l-3.9-3C.4 8.2 0 10 0 12s.4 3.8 1.1 5.3l3.9-3z"/><path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.3 0 3.2 2.7 1.1 6.7l3.9 3c1-2.9 3.8-5 7-5z"/></svg>
          Continue with Google
        </button>

        <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} data-testid="login-mode-toggle"
          className="w-full text-center text-xs text-slate-500 hover:text-white mt-5 transition-colors">
          {mode === "login" ? "New here? Create an account" : "Already have an account? Sign in"}
        </button>
      </motion.div>
    </div>
  );
}
